import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import { CheckCircle, XCircle, Loader2, Wallet } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sym } = useCurrency();
  const reference = searchParams.get('reference') || searchParams.get('trxref');
  const [status, setStatus] = useState('verifying');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!reference) {
      setStatus('error');
      return;
    }

    const verifyPayment = async () => {
      try {
        const res = await api.get(`/payments/verify/${reference}`);
        const data = res.data?.data;
        setPaymentData(data);
        if (data?.status === 'success') {
          setStatus('success');
          toast.success('Payment successful! Your wallet has been funded.');
        } else if (data?.status === 'abandoned') {
          setStatus('cancelled');
        } else {
          setStatus('pending');
        }
      } catch {
        setStatus('error');
        toast.error('Could not verify payment');
      }
    };

    verifyPayment();
  }, [reference]);

  const amount = paymentData ? (paymentData.amount / 100) : 0;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-muted" data-testid="payment-callback-page">
      <Card className="w-full max-w-md border border-border/50 shadow-xl" data-testid="payment-callback-card">
        <CardContent className="pt-8 text-center space-y-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Wallet className="h-7 w-7 text-primary" />
            <span className="text-xl font-bold text-primary" style={{fontFamily: 'DM Sans'}}>SamaFlux</span>
          </div>

          {status === 'verifying' && (
            <div className="space-y-4" data-testid="verifying-state">
              <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto" />
              <h2 className="text-xl font-semibold">Verifying Payment...</h2>
              <p className="text-muted-foreground">Please wait while we confirm your transaction.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4" data-testid="success-state">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h2 className="text-xl font-semibold text-green-600">Payment Successful!</h2>
              <p className="text-3xl font-bold text-primary">{sym}{amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              <p className="text-muted-foreground">has been added to your wallet</p>
              <p className="text-xs text-muted-foreground">Ref: {reference}</p>
              <Button
                className="w-full rounded-full bg-primary hover:bg-primary/90 mt-4"
                onClick={() => navigate('/dashboard')}
                data-testid="go-to-dashboard-button"
              >
                Go to Dashboard
              </Button>
            </div>
          )}

          {status === 'cancelled' && (
            <div className="space-y-4" data-testid="cancelled-state">
              <XCircle className="h-16 w-16 text-yellow-500 mx-auto" />
              <h2 className="text-xl font-semibold text-yellow-600">Payment Cancelled</h2>
              <p className="text-muted-foreground">You cancelled this payment. No funds were deducted.</p>
              <Button
                className="w-full rounded-full bg-primary hover:bg-primary/90 mt-4"
                onClick={() => navigate('/dashboard')}
                data-testid="go-to-dashboard-button"
              >
                Back to Dashboard
              </Button>
            </div>
          )}

          {status === 'pending' && (
            <div className="space-y-4" data-testid="pending-state">
              <Loader2 className="h-16 w-16 text-yellow-500 mx-auto" />
              <h2 className="text-xl font-semibold text-yellow-600">Payment Pending</h2>
              <p className="text-muted-foreground">Your payment is being processed. It may take a few moments.</p>
              <Button
                className="w-full rounded-full bg-primary hover:bg-primary/90 mt-4"
                onClick={() => navigate('/dashboard')}
                data-testid="go-to-dashboard-button"
              >
                Back to Dashboard
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4" data-testid="error-state">
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <h2 className="text-xl font-semibold text-red-600">Verification Failed</h2>
              <p className="text-muted-foreground">We could not verify this payment. Please contact support if funds were deducted.</p>
              <Button
                className="w-full rounded-full bg-primary hover:bg-primary/90 mt-4"
                onClick={() => navigate('/dashboard')}
                data-testid="go-to-dashboard-button"
              >
                Back to Dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentCallback;
